/** Format a UTC ISO timestamp as a friendly local-relative string.
 *
 *  Output examples:
 *    "just now"
 *    "5 minutes ago"
 *    "2 hours ago"
 *    "yesterday at 2:14 PM"
 *    "Apr 28 at 9:30 AM"
 *    "Mar 15, 2026 at 4:00 PM"
 *
 *  All input is treated as UTC; rendering uses the browser's local
 *  timezone via `toLocaleString` / `toLocaleTimeString`. Use this
 *  everywhere we render a timestamp from a JSON payload (changelog,
 *  pipeline runs, capture timestamps, etc.) so the user always sees
 *  their local clock instead of UTC or the daemon's TZ.
 *
 *  Returns the empty string on invalid input rather than throwing,
 *  so callers can use it inline without try/catch.
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";

  const now = Date.now();
  const diff = now - t;
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);

  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  if (min === 1) return "1 minute ago";
  if (min < 60) return `${min} minutes ago`;
  if (hour === 1) return "1 hour ago";
  if (hour < 24) return `${hour} hours ago`;

  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  if (day === 1) return `yesterday at ${time}`;
  if (day < 7) {
    const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
    return `${weekday} at ${time}`;
  }

  const sameYear = d.getFullYear() === new Date().getFullYear();
  const date = d.toLocaleDateString(undefined, sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
  return `${date} at ${time}`;
}

/** Absolute, locale-aware "Apr 30, 2026 at 1:14 PM" style. Used for
 *  hover tooltips on the relative renderer so the user can confirm
 *  the exact moment without doing relative math in their head. */
export function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
